import authApi from '../../api/auth'
import permisosApi from '../../api/permisos'
import router from '../../router'

const state = {
  token: localStorage.getItem('token') || null,
  user: JSON.parse(localStorage.getItem('user')) || null,
  // { es_admin, recursos: { 'arriendos.contratos': 'leer' | 'escribir', ... } } desde /auth/me/permisos
  permisos: JSON.parse(localStorage.getItem('permisos')) || null,
  loading: false,
  error: null
}

const getters = {
  isAuthenticated: state => !!state.token,
  user: state => state.user,
  loading: state => state.loading,
  error: state => state.error,
  permisos: state => state.permisos,
  esAdmin: state => !!state.permisos?.es_admin,
  // puede('arriendos.pagos') o puede('arriendos.pagos', 'escribir'); escribir incluye leer
  puede: state => (recurso, accion = 'leer') => {
    const nivel = state.permisos?.recursos?.[recurso]
    return accion === 'leer' ? !!nivel : nivel === 'escribir'
  },
  // algún permiso dentro de la categoría (para mostrar el módulo en el menú)
  puedeCategoria: state => categoria =>
    Object.keys(state.permisos?.recursos || {}).some(r => r.startsWith(categoria + '.'))
}

const actions = {
  async login({ commit, dispatch }, { username, password }) {
    // Clear any stale auth state before attempting login
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('permisos')
    commit('setToken', null)
    commit('setUser', null)
    commit('setPermisos', null)
    commit('setLoading', true)
    commit('setError', null)
    
    try {
      console.log(`Iniciando proceso de login para ${username}`)
      const response = await authApi.login(username, password)
      
      console.log('Respuesta de login:', response.status, response.statusText)
      
      // Obtener los datos de la respuesta
      const responseData = response.data;
      
      // Verificar que tenemos todos los datos necesarios
      if (!responseData.access_token) {
        console.error('No se recibió access_token en la respuesta')
        throw new Error('Token no recibido')
      }
      
      // Extraer datos correctamente
      const { access_token, token_type, username: user, role } = responseData;
      
      // Guardar token en localStorage
      const fullToken = token_type ? `${token_type} ${access_token}` : `Bearer ${access_token}`;
      localStorage.setItem('token', fullToken)
      localStorage.setItem('user', JSON.stringify({ username: user, role }))
      
      // Log para depuración
      console.log(`Token guardado: ${fullToken.substring(0, 20)}...`)
      
      // Actualizar estado
      commit('setToken', fullToken)
      commit('setUser', { username: user, role })
      await dispatch('cargarPermisos')
      
      router.push('/')
      return true
    } catch (error) {
      console.error('Error completo en login:', error)
      commit('setError', error.response?.data?.detail || 'Error al iniciar sesión')
      return false
    } finally {
      commit('setLoading', false)
    }
  },
  
  logout({ commit }) {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('permisos')
    commit('setToken', null)
    commit('setUser', null)
    commit('setPermisos', null)
    router.push('/login')
  },
  
  async register({ commit, dispatch }, { username, email, password }) {
    commit('setLoading', true)
    commit('setError', null)
    
    try {
      await authApi.register(username, email, password)
      return await dispatch('login', { username, password })
    } catch (error) {
      commit('setError', error.response?.data?.detail || 'Error al registrarse')
      return false
    } finally {
      commit('setLoading', false)
    }
  },
  
  async fetchProfile({ commit, state }) {
    if (!state.token) {
      console.warn('fetchProfile: No hay token disponible')
      return null
    }
    
    try {
      console.log('Obteniendo perfil con token:', state.token.substring(0, 20) + '...')
      const response = await authApi.getProfile()
      commit('setUser', response.data)
      return response.data
    } catch (error) {
      console.error('Error al obtener perfil:', error)
      if (error.response?.status === 401) {
        commit('setToken', null)
        commit('setUser', null)
      }
      throw error
    }
  },
  async cargarPermisos({ commit }) {
    const { data } = await permisosApi.misPermisos()
    localStorage.setItem('permisos', JSON.stringify(data))
    commit('setPermisos', data)
    return data
  },

  // Add an action to check auth status on app load
  
  async checkAuth({ commit, dispatch }) {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }

    // Check if token is valid
    // Strip possible 'Bearer ' prefix before parsing
    const rawToken = token.toLowerCase().startsWith('bearer ') ? token.substring(7) : token;
    const tokenData = parseJwt(rawToken);
    if (!tokenData || !tokenData.exp) {
      dispatch('logout');
      return false;
    }

    const now = Date.now() / 1000;
    if (tokenData.exp < now) {
      // Token expired
      dispatch('logout');
      return false;
    }

    // Token is valid - restore user state
    const user = localStorage.getItem('user');
    if (user) {
      // use correct mutation names
      commit('setUser', JSON.parse(user));
      commit('setToken', token);
      // Los permisos pueden haber cambiado desde la última visita: se piden de nuevo al abrir la app
      try {
        await dispatch('cargarPermisos');
      } catch (e) {
        if (e.response?.status === 401) return false;  // el interceptor ya manda a /login
      }
      return true;
    }

    return false;
  }
}

// Helper function (same as in api/index.js)
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

const mutations = {
  setToken(state, token) {
    state.token = token
  },
  setUser(state, user) {
    state.user = user
  },
  setLoading(state, loading) {
    state.loading = loading
  },
  setError(state, error) {
    state.error = error
  },
  setPermisos(state, permisos) {
    state.permisos = permisos
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}